// Thread management
const THREAD_STORAGE_KEY = 'openai_thread_id';
const API_KEY = process.env.REACT_APP_OPENAI_API_KEY;
const ASSISTANT_ID = process.env.REACT_APP_OPENAI_ASSISTANT_ID;
const API_BASE = 'https://api.openai.com/v1';

const saveThreadId = (threadId) => {
  if (threadId) {
    localStorage.setItem(THREAD_STORAGE_KEY, threadId);
  } else {
    localStorage.removeItem(THREAD_STORAGE_KEY);
  }
};

const getThreadId = () => {
  return localStorage.getItem(THREAD_STORAGE_KEY);
};

const createThread = async () => {
  try {
    const currentThreadId = getThreadId();
    if (currentThreadId) {
      try {
        const response = await fetch(`${API_BASE}/threads/${currentThreadId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${API_KEY}`,
            'OpenAI-Beta': 'assistants=v2'
          }
        });
        if (response.ok) {
          const thread = await response.json();
          if (thread && thread.id && thread.id.startsWith('thread_')) {
            return thread.id;
          }
        }
      } catch {
        saveThreadId(null);
      }
    }
    const response = await fetch(`${API_BASE}/threads`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${API_KEY}`,
        'Content-Type': 'application/json',
        'OpenAI-Beta': 'assistants=v2'
      },
      body: JSON.stringify({
        messages: []
      })
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to create thread: ${errorData.error?.message || 'Unknown error'}`);
    }
    const thread = await response.json();
    if (!thread || !thread.id || !thread.id.startsWith('thread_')) {
      throw new Error('Invalid thread ID returned from create');
    }
    saveThreadId(thread.id);
    return thread.id;
  } catch {
    saveThreadId(null);
    throw new Error('Failed to create thread');
  }
};

const clearThread = async () => {
  try {
    const currentThreadId = getThreadId();
    if (currentThreadId) {
      await fetch(`${API_BASE}/threads/${currentThreadId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${API_KEY}`,
          'OpenAI-Beta': 'assistants=v2'
        }
      });
    }
  } catch {
    // Silent fail on clear
  } finally {
    saveThreadId(null);
  }
};

// Message handling
const sendMessage = async (messageContent, onStream, assistantIdOverride) => {
  if (!messageContent) {
    throw new Error('Message content is required');
  }

  try {
    const threadId = await createThread();

    if (!threadId || !threadId.startsWith('thread_')) {
      throw new Error('Invalid thread ID');
    }

    const messageResponse = await fetch(`${API_BASE}/threads/${threadId}/messages`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${API_KEY}`,
        'Content-Type': 'application/json',
        'OpenAI-Beta': 'assistants=v2'
      },
      body: JSON.stringify({
        role: 'user',
        content: messageContent
      })
    });

    if (!messageResponse.ok) {
      throw new Error('Failed to add message to thread');
    }

    const runResponse = await fetch(`${API_BASE}/threads/${threadId}/runs`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${API_KEY}`,
        'Content-Type': 'application/json',
        'OpenAI-Beta': 'assistants=v2'
      },
      body: JSON.stringify({
        assistant_id: assistantIdOverride ? assistantIdOverride : ASSISTANT_ID
      })
    });

    if (!runResponse.ok) {
      throw new Error('Failed to create run');
    }

    const run = await runResponse.json();

    if (!run || !run.id) {
      throw new Error('Failed to create run');
    }

    // --- TRUE STREAMING VIA VERCEL PROXY ---
    let fullMessage = '';
    let gotAny = false;
    try {
      const streamResponse = await fetch('/api/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ thread_id: threadId, run_id: run.id }),
      });
      if (!streamResponse.ok || !streamResponse.body) {
        throw new Error('Streaming not supported or failed to connect');
      }
      const reader = streamResponse.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let shouldStop = false;
      while (!shouldStop) {
        const { value, done } = await reader.read();
        if (done) {
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        let lines = buffer.split('\n');
        buffer = lines.pop(); // last line may be incomplete
        for (const line of lines) {
          if (line.startsWith('data:')) {
            const data = line.slice(5).trim();
            if (data === '[DONE]') {
              if (gotAny) return fullMessage;
              shouldStop = true;
              break;
            }
            try {
              const event = JSON.parse(data);
              // Look for message content delta
              if (event.event === 'thread.message.delta') {
                const delta = event.data?.delta;
                if (delta && delta.content && Array.isArray(delta.content)) {
                  for (const part of delta.content) {
                    if (part.type === 'text' && part.text && part.text.value) {
                      fullMessage += part.text.value;
                      gotAny = true;
                      if (onStream) onStream(fullMessage);
                    }
                  }
                }
              }
            } catch {
              // Ignore JSON parse errors for non-data lines
            }
          }
        }
        if (shouldStop) {
          break;
        }
      }
      if (gotAny) return fullMessage;
    } catch (err) {
      // If streaming fails, fallback to polling
      console.warn('Streaming via proxy failed, falling back to polling:', err);
    }
    // --- FALLBACK: POLLING (existing logic) ---
    let attempts = 0;
    const maxAttempts = 20;
    const pollInterval = 10;
    while (attempts < maxAttempts) {
      const statusResponse = await fetch(`${API_BASE}/threads/${threadId}/runs/${run.id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${API_KEY}`,
          'OpenAI-Beta': 'assistants=v2'
        }
      });
      if (!statusResponse.ok) {
        throw new Error('Failed to get run status');
      }
      const status = await statusResponse.json();
      if (status.status === 'completed') {
        break;
      }
      if (status.status === 'failed') {
        throw new Error(`Assistant run failed: ${status.last_error?.message || 'Unknown'}`);
      }
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      attempts++;
    }
    const messagesResponse = await fetch(`${API_BASE}/threads/${threadId}/messages?order=desc&limit=1`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${API_KEY}`,
        'OpenAI-Beta': 'assistants=v2'
      }
    });
    if (!messagesResponse.ok) {
      throw new Error('Failed to get messages');
    }
    const messages = await messagesResponse.json();
    const assistantMessage = messages.data[0];
    if (!assistantMessage) {
      throw new Error('No assistant message found');
    }
    const content = assistantMessage.content[0];
    if (content.type !== 'text') {
      throw new Error('Unexpected message content type');
    }
    const message = content.text.value;
    // Letter-by-letter streaming effect
    let displayed = '';
    const letterDelay = 0; // ms per letter (instant)
    for (let i = 0; i < message.length; i++) {
      displayed += message[i];
      if (onStream) {
        onStream(displayed);
      }
      await new Promise(resolve => setTimeout(resolve, letterDelay));
    }
    return message;
  } catch (error) {
    throw new Error(`sendMessage failed: ${error.message}`);
  }
};

export {
  createThread,
  clearThread,
  sendMessage
};